import { z } from 'zod';
import { UI_LOCALE_CODES } from './i18n';
import { LANGUAGE_CODES } from './languages';
import { ASSET_KINDS } from './types';
import { parseYouTubeId } from './youtube';

export const MAX_UPLOAD_BYTES = 20 * 1024 ** 3; // 20 GB

const discordId = z.string().regex(/^\d{15,22}$/, 'Must be a Discord user ID (a long number)');
const language = z
  .string()
  .refine((code) => (LANGUAGE_CODES as string[]).includes(code), 'Unknown language');
const cents = z.number().int().min(0).max(100_000_000);
const currency = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code like USD');
const filename = z.string().trim().min(1).max(255);
const contentType = z.string().max(255).default('application/octet-stream');
const uiLocale = z.enum(UI_LOCALE_CODES);
const duration = z.number().positive().max(24 * 60 * 60).optional();

/** File types accepted for a dub submission (the contractor's voice track only). */
export const DUB_AUDIO_EXTENSIONS = ['wav', 'mp3'];

/** True for a .wav or .mp3 file. Judged by extension, since browsers report audio types inconsistently. */
export function isAllowedDubFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return DUB_AUDIO_EXTENSIONS.includes(ext);
}

export const createContractorSchema = z.object({
  discordId,
  languages: z.array(language).default([]),
  defaultRateCents: cents.default(0),
  currency: currency.default('USD'),
});
export type CreateContractorInput = z.input<typeof createContractorSchema>;

export const updateContractorSchema = z.object({
  languages: z.array(language).optional(),
  defaultRateCents: cents.optional(),
  currency: currency.optional(),
  active: z.boolean().optional(),
});
export type UpdateContractorInput = z.input<typeof updateContractorSchema>;

const youtubeUrl = z
  .string()
  .trim()
  .refine((v) => parseYouTubeId(v) !== null, 'Not a recognisable YouTube link');

export const createProjectSchema = z.object({
  title: z.string().trim().min(1).max(200),
  youtubeUrl,
  notes: z.string().max(5000).default(''),
});
export type CreateProjectInput = z.input<typeof createProjectSchema>;

export const updateProjectSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  youtubeUrl: youtubeUrl.optional(),
  notes: z.string().max(5000).optional(),
  archived: z.boolean().optional(),
});
export type UpdateProjectInput = z.input<typeof updateProjectSchema>;

export const createAssignmentSchema = z.object({
  contractorId: discordId,
  language,
  rateCents: cents.optional(),
  currency: currency.optional(),
});
export type CreateAssignmentInput = z.input<typeof createAssignmentSchema>;

export const updateAssignmentSchema = z.object({
  rateCents: cents.optional(),
  currency: currency.optional(),
  paid: z.boolean().optional(),
});
export type UpdateAssignmentInput = z.input<typeof updateAssignmentSchema>;

export const initiateUploadSchema = z.discriminatedUnion('purpose', [
  z.object({
    purpose: z.literal('asset'),
    projectId: z.string().min(1),
    kind: z.enum(ASSET_KINDS),
    filename,
    size: z.number().int().positive().max(MAX_UPLOAD_BYTES),
    contentType,
    durationSeconds: duration,
  }),
  z.object({
    purpose: z.literal('submission'),
    assignmentId: z.string().min(1),
    filename,
    size: z.number().int().positive().max(MAX_UPLOAD_BYTES),
    contentType,
    durationSeconds: duration,
    note: z.string().max(2000).default(''),
  }),
]);
export type InitiateUploadInput = z.input<typeof initiateUploadSchema>;

export const signPartsSchema = z.object({
  uploadId: z.string().min(1),
  partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(100),
});
export type SignPartsInput = z.input<typeof signPartsSchema>;

export const completeUploadSchema = z.object({
  uploadId: z.string().min(1),
  parts: z
    .array(z.object({ partNumber: z.number().int().min(1).max(10_000), etag: z.string().min(1) }))
    .min(1),
});
export type CompleteUploadInput = z.input<typeof completeUploadSchema>;

export const abortUploadSchema = z.object({ uploadId: z.string().min(1) });

export const reviewSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve'), note: z.string().max(5000).default('') }),
  z.object({ decision: z.literal('request_changes'), note: z.string().trim().min(1).max(5000) }),
]);
export type ReviewInput = z.input<typeof reviewSchema>;

export const markNotificationsReadSchema = z.object({
  ids: z.array(z.string().min(1)).max(200).optional(),
  all: z.boolean().optional(),
});
export type MarkNotificationsReadInput = z.input<typeof markNotificationsReadSchema>;

/** Loudness target for finished mixes: YouTube's normalization level. */
export const MIX_TARGET_LUFS = -14;

export const createMixSchema = z.object({
  voiceGainDb: z.number().min(-24).max(24),
  sfxGainDb: z.number().min(-24).max(24),
  normalize: z.boolean(),
});
export type CreateMixInput = z.input<typeof createMixSchema>;

export const updateSettingsSchema = z
  .object({
    locale: uiLocale.optional(),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
  })
  .refine((v) => v.locale !== undefined || v.email !== undefined, 'Nothing to update');
export type UpdateSettingsInput = z.input<typeof updateSettingsSchema>;

export const translateSchema = z.object({
  texts: z.array(z.string().max(5000)).min(1).max(10),
  source: uiLocale,
  target: uiLocale,
});
export type TranslateInput = z.input<typeof translateSchema>;
